// Utility service for handling fax operations
import { supabase } from '../supabaseClient';

import { getAuthHeaders } from './apiAuth';
class FaxService {
  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  }

  /**
   * Convert file to base64 for eFax API
   */
  async convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Send a fax via eFax API
   */
  async sendFax({
    toFaxNumber,
    file,
    fileName,
    subject = '',
    comments = '',
    recipientName = '',
    organization = '',
    coverPage = 'Standard'
  }) {
    try {
      // Convert file to base64
      const fileContent = await this.convertFileToBase64(file);

      const response = await fetch(`${this.apiBaseUrl}/api/send-fax`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          to_fax_number: toFaxNumber,
          file_content: fileContent,
          file_name: fileName || file.name,
          file_type: file.type,
          subject,
          comments,
          cover_page: coverPage,
          recipient_name: recipientName,
          organization
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to send fax');
      }

      return result;
    } catch (error) {
      console.error('Fax sending error:', error);
      throw error;
    }
  }

  /**
   * Check fax status
   */
  async getFaxStatus(faxId) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/get-fax-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          fax_id: faxId
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to get fax status');
      }

      return result;
    } catch (error) {
      console.error('Fax status check error:', error);
      throw error;
    }
  }

  /**
   * Send fax from Supabase storage URL
   */
  async sendFaxFromUrl({
    toFaxNumber,
    fileUrl,
    fileName,
    subject = '',
    comments = '',
    recipientName = '',
    organization = '',
    coverPage = 'Standard'
  }) {
    try {
      // Download file from Supabase storage
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error('Failed to download file from storage');
      }

      const blob = await response.blob();
      const file = new File([blob], fileName, { type: blob.type });

      return await this.sendFax({
        toFaxNumber,
        file,
        fileName,
        subject,
        comments,
        recipientName,
        organization,
        coverPage
      });
    } catch (error) {
      console.error('Fax from URL error:', error);
      throw error;
    }
  }

  /**
   * Log fax activity to database
   */
  async logFaxActivity({
    submissionId,
    faxId,
    toFaxNumber,
    fileName,
    status,
    error = null
  }) {
    try {
      const { error: dbError } = await supabase
        .from('fax_logs')
        .insert({
          submission_id: submissionId,
          fax_id: faxId,
          to_fax_number: toFaxNumber,
          file_name: fileName,
          status,
          error_message: error?.message || null,
          sent_at: new Date().toISOString()
        });

      if (dbError) {
        console.error('Failed to log fax activity:', dbError);
      }
    } catch (error) {
      console.error('Fax logging error:', error);
    }
  }

  /**
   * Format fax number for eFax (must be 11 digits)
   */
  formatFaxNumber(phoneNumber) {
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // If 10 digits, add 1 for US
    if (cleaned.length === 10) {
      return `1${cleaned}`;
    }
    
    // If 11 digits and starts with 1, return as is
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned;
    }
    
    // Otherwise, return as is (will be validated by eFax)
    return cleaned;
  }

  /**
   * Validate fax number format
   */
  isValidFaxNumber(faxNumber) {
    const cleaned = faxNumber.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 11;
  }
}

export default new FaxService();
